import { FC, useContext, useEffect, useMemo } from "react";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { EnterpriseComplianceFiltersContext } from "../contexts/EcdFiltersContext";
import { EcdColorsContext } from "../contexts/EcdColorsContext";
import { EcdTilesEnum } from "../interfaces";
import EcdCompositeFillableGapsFlyout from "../flyouts/EcdCompositeFillableGapsFlyout";
import { tileFlyoutLink } from "../flyouts/EcdDrillInConfig";
import CompositeFillableGapsChart from "../components/CompositeFillableGapsChart";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";

const CompositeFillableGaps: FC = () => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);
    const dashboardContext = useContext(EnterpriseComplianceDashboardContext);
    const colorsContext = useContext(EcdColorsContext);
    const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
    const title = "Composite Fillable Gaps";

    /** CompositeFillableGaps' position on the page/in the DOM changes at 1200px viewport width.
     * This causes the component to unmount and remount, which would default it back into its loading
     * state without logical safeguards based on the context.
     * We could also use the CSS order property to rearrange the widgets, but that presents
     * accessibility issues.
     */
    useEffect(() => {
        if (!dashboardContext.compositeFillableGapsData) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.CompositeFillableGaps, true);
        }
    }, []);

    useEffect(() => {
        if (!!filtersContext.apiReadyFilters && filtersContext.isDashboardLoading) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.CompositeFillableGaps, true);
        }
    }, [filtersContext.apiReadyFilters]);

    useEffect(() => {
        if (dashboardContext.compositeFillableGapsData) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.CompositeFillableGaps, false);
        }
    }, [dashboardContext.compositeFillableGapsData]);

    const flyoutLinkTitle = useMemo(() => {
        if (!featureFlags.has(Features.ENABLE_ECD_DRILLINS) || dashboardContext.isDataEmptyMap.get(EcdTilesEnum.CompositeFillableGaps)) {
            return title;
        }

        return tileFlyoutLink(
            <EcdCompositeFillableGapsFlyout />,
            "Composite Fillable Gaps Details",
            filtersContext.isDashboardLoading || dashboardContext.isTileLoadingMap.get(EcdTilesEnum.CompositeFillableGaps),
            "composite-fillable-gaps__title-flyout",
            title,
        );
    }, [
        filtersContext.isDashboardLoading,
        featureFlags.has(Features.ENABLE_ECD_DRILLINS),
        dashboardContext.isDataEmptyMap.get(EcdTilesEnum.CompositeFillableGaps),
        dashboardContext.isTileLoadingMap.get(EcdTilesEnum.CompositeFillableGaps)
    ]);

    return (
        <DashboardTile
            loading={filtersContext.isDashboardLoading || dashboardContext.isTileLoadingMap.get(EcdTilesEnum.CompositeFillableGaps)}
            title={flyoutLinkTitle}
            widthClass="col-12 col-md-6 col-xl-3"
        >
          <CompositeFillableGapsChart
            compositeFillableGapsData={dashboardContext.compositeFillableGapsData}
            fillableGapsPrimary={colorsContext.fillableGapsPrimary}
            fillableGapsSecondary={colorsContext.fillableGapsSecondary}
            unfillableGaps={colorsContext.unfillableGaps}
          />
        </DashboardTile>
    );
}

export default CompositeFillableGaps;
